import { useCallback, useState } from "react";
import {
  listGoalsEngineersReq,
  exportGoalsReq,
  createUserGoalReq,
  getUserGoalReq,
  updateUserGoalReq,
  confirmUserGoalSetupReq,
  markGoalItemReq,
  submitUserGoalReviewReq,
  confirmUserGoalReviewReq,
  rejectUserGoalReviewReq,
  getUserGoalHistoryReq,
} from "services/api";

const parseErrorMessage = (err, fallbackMessage) =>
  err?.response?.data?.message ||
  (err?.response?.data?.errors && err.response.data.errors[0]?.msg) ||
  fallbackMessage;

function useGoals({ adminId, teamId, channelId }) {
  const [engineers, setEngineers] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const [activeGoal, setActiveGoal] = useState(null);
  const [isGoalLoading, setIsGoalLoading] = useState(false);
  const [engineerGoals, setEngineerGoals] = useState(null);
  const [isEngineerGoalsLoading, setIsEngineerGoalsLoading] = useState(false);

  const fetchEngineers = useCallback(
    async ({ search, pageNumber, pageLimit } = {}) => {
      if (!adminId || !teamId || !channelId) return;
      setIsLoading(true);
      try {
        const { data } = await listGoalsEngineersReq({
          admin_id: adminId,
          team_id: teamId,
          channel_id: channelId,
          params: { search, pageNumber, pageLimit },
        });
        setEngineers(data.engineers || []);
        setTotal(data.total || 0);
      } catch (err) {
        setErrorMessage(parseErrorMessage(err, "Failed to load engineers."));
      }
      setIsLoading(false);
    },
    [adminId, teamId, channelId]
  );

  // Downloads the CSV. Because the request asks for a blob, an error body
  // arrives as a blob too, so its JSON message has to be read out of it.
  const exportGoals = useCallback(
    async ({ templateId, year }) => {
      if (!adminId || !teamId || !channelId) return false;
      try {
        const { data, headers } = await exportGoalsReq({
          admin_id: adminId,
          team_id: teamId,
          channel_id: channelId,
          params: { template_id: templateId, year },
        });
        const fileName =
          /filename="?([^";]+)"?/.exec(headers["content-disposition"] || "")?.[1] ||
          `goals-${year}.csv`;
        const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return true;
      } catch (err) {
        let message = null;
        try {
          const body = err?.response?.data;
          message = body instanceof Blob ? JSON.parse(await body.text()).message : null;
        } catch (parseErr) {
          message = null;
        }
        setErrorMessage(message || "Failed to export goals.");
        return false;
      }
    },
    [adminId, teamId, channelId]
  );

  const fetchUserGoal = useCallback(
    async (id) => {
      if (!adminId || !id) return null;
      setIsGoalLoading(true);
      try {
        const { data } = await getUserGoalReq({ admin_id: adminId, id });
        setActiveGoal(data);
        setIsGoalLoading(false);
        return data;
      } catch (err) {
        setErrorMessage(parseErrorMessage(err, "Failed to load goal period."));
        setIsGoalLoading(false);
        return null;
      }
    },
    [adminId]
  );

  // { engineer, periods } - every period this engineer has had, newest first.
  const fetchEngineerGoals = useCallback(
    async (userChannelId) => {
      if (!adminId || !userChannelId) return;
      setIsEngineerGoalsLoading(true);
      try {
        const { data } = await getUserGoalHistoryReq({
          admin_id: adminId,
          user_channel_id: userChannelId,
        });
        setEngineerGoals(data);
      } catch (err) {
        setErrorMessage(parseErrorMessage(err, "Failed to load engineer goals."));
      }
      setIsEngineerGoalsLoading(false);
    },
    [adminId]
  );

  const withFeedback = async (action, fallbackMessage, successText) => {
    try {
      setErrorMessage(null);
      setSuccessMessage(null);
      const data = await action();
      setSuccessMessage(successText);
      return data;
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, fallbackMessage));
      return null;
    }
  };

  const createUserGoal = (body) =>
    withFeedback(
      async () => {
        const { data } = await createUserGoalReq({
          admin_id: adminId,
          team_id: teamId,
          channel_id: channelId,
          body,
        });
        return data;
      },
      "Failed to set up goals.",
      body.direct_setup
        ? "Goals set up and active."
        : "Goal period drafted. It needs setup confirmation from another admin."
    );

  const updateUserGoal = (id, body) =>
    withFeedback(
      async () => {
        const { data } = await updateUserGoalReq({ admin_id: adminId, id, body });
        setActiveGoal(data);
        return data;
      },
      "Failed to update goals.",
      "Goal period updated."
    );

  const confirmSetup = (id) =>
    withFeedback(
      async () => {
        const { data } = await confirmUserGoalSetupReq({ admin_id: adminId, id });
        return data;
      },
      "Failed to confirm setup.",
      "Goal setup confirmed - the period is now active."
    );

  const markGoalItem = (goalItemId, body) =>
    withFeedback(
      async () => {
        const { data } = await markGoalItemReq({
          admin_id: adminId,
          goal_item_id: goalItemId,
          body,
        });
        return data;
      },
      "Failed to save goal marking.",
      null
    );

  const submitReview = (id) =>
    withFeedback(
      async () => {
        const { data } = await submitUserGoalReviewReq({ admin_id: adminId, id });
        return data;
      },
      "Failed to submit review.",
      "Review submitted. It needs confirmation from another admin."
    );

  const confirmReview = (id) =>
    withFeedback(
      async () => {
        const { data } = await confirmUserGoalReviewReq({ admin_id: adminId, id });
        return data;
      },
      "Failed to confirm review.",
      "Review confirmed and completed."
    );

  const rejectReview = (id, notes) =>
    withFeedback(
      async () => {
        const { data } = await rejectUserGoalReviewReq({ admin_id: adminId, id, notes });
        return data;
      },
      "Failed to reject review.",
      "Review sent back for corrections."
    );

  return {
    engineers,
    total,
    isLoading,
    errorMessage,
    successMessage,
    fetchEngineers,
    exportGoals,
    activeGoal,
    isGoalLoading,
    fetchUserGoal,
    setActiveGoal,
    engineerGoals,
    isEngineerGoalsLoading,
    fetchEngineerGoals,
    createUserGoal,
    updateUserGoal,
    confirmSetup,
    markGoalItem,
    submitReview,
    confirmReview,
    rejectReview,
    clearError: () => setErrorMessage(null),
    clearSuccess: () => setSuccessMessage(null),
    setErrorMessage,
  };
}

export default useGoals;
